/**
 * Document I/O: JSON serialization, SVG export, and viewport raster export.
 *
 * The cell tree maps naturally onto nested SVG <g> elements: each level is
 * translate(offset) scale(0.5), which keeps the exported file small and the
 * geometry exact at any depth (no accumulated float error, powers of two).
 * Eraser strokes are expressed as SVG masks so the export matches the screen.
 */

import { CELL_EXTENT, WORLD_EXTENT, Cell, QUAD_OFFSETS } from "./cell";
import { Camera } from "./camera";
import { Element, Stroke, WorldData } from "./model";
import { outlineToSvgPath, ribbonOutline, strokeWidthAt, pressureOf } from "./strokes";
import { World } from "./world";
import { Renderer } from "./renderer";
import { drawGrid } from "./grid";

export const DOCUMENT_FORMAT = "frond-document";
export const DOCUMENT_VERSION = 1;

export function serializeWorld(world: World, camera?: Camera): string {
  const data = world.toData();
  const doc = {
    format: DOCUMENT_FORMAT,
    version: DOCUMENT_VERSION,
    savedAt: new Date().toISOString(),
    camera: camera
      ? {
          focusCellId: camera.focusCellId,
          x: camera.x,
          y: camera.y,
          scale: camera.scale,
          rotation: camera.rotation,
        }
      : undefined,
    world: data,
  };
  return JSON.stringify(doc);
}

export interface Deserialized {
  world: WorldData;
  camera?: {
    focusCellId: string;
    x: number;
    y: number;
    scale: number;
    rotation: number;
  };
}

export function deserializeWorld(json: string): Deserialized {
  const parsed: unknown = JSON.parse(json);
  if (typeof parsed !== "object" || parsed === null) {
    throw new Error("Invalid document");
  }
  const doc = parsed as { format?: string; world?: WorldData; camera?: Deserialized["camera"] };
  if (doc.format !== DOCUMENT_FORMAT || !doc.world || doc.world.version !== 1) {
    throw new Error(`Unsupported document format (expected ${DOCUMENT_FORMAT} v${DOCUMENT_VERSION})`);
  }
  if (doc.world.cells) {
    for (const [cellId, els] of Object.entries(doc.world.cells)) {
      doc.world.cells[cellId] = els.filter((el) => !(el.kind === "stroke" && el.style?.tool === "eraser"));
    }
  }
  return { world: doc.world, camera: doc.camera };
}

const SVG_NS = "http://www.w3.org/2000/svg";

function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Export the whole world as SVG. The output is a tree of nested groups that
 * mirrors the cell tree exactly — a "deep zoom" SVG.
 */
export function exportSvg(world: World, bgColor: string): string {
  const { tree } = world;
  const root = tree.root;
  const parts: string[] = [];
  parts.push(
    `<svg xmlns="${SVG_NS}" viewBox="-${CELL_EXTENT} -${CELL_EXTENT} ${CELL_EXTENT * 2} ${CELL_EXTENT * 2}">`,
  );
  parts.push(`<rect x="-${CELL_EXTENT}" y="-${CELL_EXTENT}" width="${CELL_EXTENT * 2}" height="${CELL_EXTENT * 2}" fill="${escapeXml(bgColor)}"/>`);
  parts.push(`<rect x="-${WORLD_EXTENT}" y="-${WORLD_EXTENT}" width="${WORLD_EXTENT * 2}" height="${WORLD_EXTENT * 2}" fill="none" stroke="#b3b0a4" stroke-width="0.01"/>`);
  emitCellSvg(root, parts, world);
  parts.push("</svg>");
  return parts.join("\n");
}

let svgMaskCounter = 0;

function emitCellSvg(cell: Cell, parts: string[], world: World): void {
  const layers = world.layers.filter((l) => l.visible && l.opacity > 0);
  const attrs = cellTransformAttr(world.tree, cell.id);

  const elsByLayer = new Map<string, Element[]>();
  for (const layer of layers) {
    const els = world.elementsInCell(cell.id, layer.id);
    if (els.length) elsByLayer.set(layer.id, els);
  }

  if (elsByLayer.size > 0) {
    parts.push(`<g${attrs}>`);
    for (const layer of layers) {
      const els = elsByLayer.get(layer.id);
      if (!els) continue;
      const eraseStrokes = els.filter((e) => e.kind === "stroke" && e.style?.tool === "eraser");
      const groupAttrs =
        layer.blend === "normal"
          ? ` opacity="${layer.opacity}"`
          : ` opacity="${layer.opacity}" style="mix-blend-mode:${layer.blend}"`;
      const maskId = `mask-${++svgMaskCounter}`;
      parts.push(`<g${groupAttrs}>`);
      if (eraseStrokes.length > 0) {
        parts.push(`<mask id="${maskId}">`);
        parts.push(`<rect x="-${CELL_EXTENT}" y="-${CELL_EXTENT}" width="${CELL_EXTENT * 2}" height="${CELL_EXTENT * 2}" fill="white"/>`);
        for (const er of eraseStrokes) {
          const stroke = er as Stroke;
          parts.push(
            `<path d="${outlineToSvgPath(ribbonOutline(stroke.points, (p) => strokeWidthAt(stroke, pressureOf(p))).path)}" fill="black"/>`,
          );
        }
        parts.push(`</mask>`);
        parts.push(`<g mask="url(#${maskId})">`);
      }
      for (const el of els) {
        if (el.kind === "fill") emitElementSvg(el, parts);
      }
      for (const el of els) {
        if (el.kind !== "fill") emitElementSvg(el, parts);
      }
      if (eraseStrokes.length > 0) parts.push(`</g>`);
      parts.push(`</g>`);
    }
  }

  const children = (Object.values(cell.children).filter(Boolean) as Cell[]);
  if (children.length > 0) {
    parts.push(`<g${attrs}>`);
    for (const child of children) emitCellSvg(child, parts, world);
    parts.push(`</g>`);
  }

  if (elsByLayer.size > 0) parts.push(`</g>`);
}

function cellTransformAttr(_tree: World["tree"], cellId: string): string {
  if (cellId === "") return "";
  // Reconstruct the transform from the path: M = T0 ∘ T1 ∘ ... where each
  // level is translate(offset) ∘ scale(0.5) (root-most level first).
  let a = 1;
  let b = 0;
  let c = 0;
  let d = 1;
  let e = 0;
  let f = 0;
  for (const ch of cellId) {
    const [ox, oy] = QUAD_OFFSETS[Number(ch)]!;
    e = e + a * ox + c * oy;
    f = f + b * ox + d * oy;
    a *= 0.5;
    b *= 0.5;
    c *= 0.5;
    d *= 0.5;
  }
  return ` transform="matrix(${a} ${b} ${c} ${d} ${e} ${f})"`;
}

function emitElementSvg(el: Element, parts: string[]): void {
  switch (el.kind) {
    case "stroke": {
      if (el.style.tool === "eraser") return; // handled via masks
      const ribbon = ribbonOutline(el.points, (p) => strokeWidthAt(el, pressureOf(p)));
      parts.push(
        `<path d="${outlineToSvgPath(ribbon.path)}" fill="${escapeXml(el.style.color)}" fill-opacity="${el.style.opacity}"/>`,
      );
      break;
    }
    case "shape": {
      const tag = el.shape === "rect" ? "rect" : "ellipse";
      if (el.shape === "rect") {
        parts.push(
          `<rect x="${el.x}" y="${el.y}" width="${el.w}" height="${el.h}" fill="${el.filled ? escapeXml(el.color) : "none"}" stroke="${escapeXml(el.color)}" stroke-width="${el.size}" opacity="${el.opacity}"/>`,
        );
      } else {
        parts.push(
          `<ellipse cx="${el.x + el.w / 2}" cy="${el.y + el.h / 2}" rx="${Math.abs(el.w) / 2}" ry="${Math.abs(el.h) / 2}" fill="${el.filled ? escapeXml(el.color) : "none"}" stroke="${escapeXml(el.color)}" stroke-width="${el.size}" opacity="${el.opacity}"/>`,
        );
      }
      break;
    }
    case "fill": {
      if (el.points.length < 3) break;
      const pts = el.points.map((q) => `${q.x},${q.y}`).join(" ");
      parts.push(
        `<polygon points="${pts}" fill="${escapeXml(el.color)}" opacity="${el.opacity}"/>`,
      );
      break;
    }
    case "text": {
      parts.push(
        `<text x="${el.x}" y="${el.y + el.fontSize}" font-size="${el.fontSize}" font-family="${escapeXml(el.fontFamily)}" fill="${escapeXml(el.color)}">${escapeXml(el.text)}</text>`,
      );
      break;
    }
    case "image": {
      parts.push(
        `<image x="${el.x}" y="${el.y}" width="${el.w}" height="${el.h}" opacity="${el.opacity}" href="${el.src}"/>`,
      );
      break;
    }
  }
}

/**
 * Render the current viewport at a higher resolution into a canvas.
 * Used for PNG/JPG export at any resolution ("screenshot tool").
 */
export function renderViewport(
  renderer: Renderer,
  camera: Camera,
  scaleFactor: number,
  onReady: (canvas: HTMLCanvasElement) => void,
  gridOpts?: Parameters<typeof drawGrid>[5],
): void {
  const { w, h } = renderer.size;
  const out = document.createElement("canvas");
  out.width = Math.round(w * scaleFactor);
  out.height = Math.round(h * scaleFactor);
  const ctx = out.getContext("2d");
  if (!ctx) return;
  renderer.render(camera, { ctx, width: w, height: h, dpr: scaleFactor });
  if (gridOpts) drawGrid(ctx, camera, w, h, scaleFactor, gridOpts);
  renderer.drawCanvasBorder(ctx, camera, w, h, scaleFactor);
  onReady(out);
}

export function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Export failed"))),
      type,
      quality,
    );
  });
}
